import { z } from 'zod';
import { IConfigArea, configAreaSchema } from './IConfigArea';
import { IBaseFloor, baseFloorSchema } from './IBaseFloor';
import { NotificationTypes } from './NotificationTypes.enum';
import { IProjectCertificate, projectCertificateSchema } from './IProjectCertificate';
import { minimizedProfessionSchema } from './IMinimizedProfession';

export const PERMITTED_MODE = 'permitted';
export const RESTRICTED_MODE = 'restricted';

export const RESTRICTED_MODES = [RESTRICTED_MODE, PERMITTED_MODE] as const;

export type RestrictedModeTypes = (typeof RESTRICTED_MODES)[number];
export type PermittedModeType = typeof PERMITTED_MODE;

// Zod schema for IRestrictedAreaTriggerShared<IMinimizedProfession> (the concrete type used in API responses)
export const restrictedAreaTriggerSchema = z.object({
	_id: z.string().optional(),
	projectId: z.string(),
	profession: z
		.object({
			professions: z.array(minimizedProfessionSchema),
			isAllSelected: z.boolean(),
			mode: z.enum(RESTRICTED_MODES),
		})
		.optional(),
	certificate: z
		.object({
			certificates: z.array(projectCertificateSchema),
			isAllSelected: z.boolean(),
			mode: z.literal(PERMITTED_MODE),
		})
		.optional(),
	areas: z.array(configAreaSchema).optional(),
	floors: z.array(baseFloorSchema).optional(),
	type: z.literal(NotificationTypes.RestrictedArea),
});

export type IRestrictedAreaTrigger = z.infer<typeof restrictedAreaTriggerSchema>;

/**
 * The REQUEST body for create and update. `restrictedAreaTriggerSchema` stays
 * the response shape — it is shared between both directions, and omitting
 * fields from it in place would break every response type.
 *
 * `projectId` is omitted rather than ignored: the project arrives in the path,
 * and a body that cannot carry one cannot move a trigger between projects.
 * `_id` likewise — the id is the address, never the payload.
 *
 * This also fixes a live 400. The update route declared the FULL schema, which
 * requires `projectId`, while its only caller
 * (`RestrictedAreaEditDialog.tsx`) builds a `Partial<IRestrictedAreaTrigger>`
 * without one and casts it past the compiler — so Zod rejected every
 * restricted-area edit in production.
 */
export const restrictedAreaTriggerBodySchema = restrictedAreaTriggerSchema.omit({ _id: true, projectId: true });

export type IRestrictedAreaTriggerBody = z.infer<typeof restrictedAreaTriggerBodySchema>;

export interface IRestrictedAreaTriggerShared<T> {
	_id?: string;
	projectId: string;
	profession?: {
		professions: T[];
		isAllSelected: boolean;
		mode: RestrictedModeTypes;
	};
	certificate?: {
		certificates: IProjectCertificate[];
		isAllSelected: boolean;
		mode: PermittedModeType;
	};
	areas?: IConfigArea[];
	floors?: IBaseFloor[];
	type: NotificationTypes.RestrictedArea;
}
